import React, { useState, useEffect } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  draft:            { bg:'#F2EFE7', color:'#556B2F', label:'Draft' },
  in_progress:      { bg:'#EDF4FF', color:'#1E4FAD', label:'In Progress' },
  pending_approval: { bg:'#FFF3DC', color:'#8B5E0A', label:'Pending Approval' },
  approved:         { bg:'#E8F5EB', color:'#2E6B3A', label:'Approved' },
  rejected:         { bg:'#FDEAEA', color:'#8F2E24', label:'Rejected' },
};

const COUNT_TYPES = [
  { value:'cycle', label:'Cycle Count' },
  { value:'weekly', label:'Weekly Count' },
  { value:'monthly', label:'Monthly Count' },
  { value:'management', label:'Management Count' },
];

export default function StockCounts() {
  const { user } = useAuth();
  const canWrite = ['super_admin','admin','inventory_officer'].includes(user?.role);
  const canApprove = ['super_admin','admin'].includes(user?.role);

  const [counts, setCounts] = useState([]);
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedLines, setSelectedLines] = useState([]);
  const [loadingLines, setLoadingLines] = useState(false);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      get('/inventory-control/stock-counts'),
      get('/inventory-control/skus')
    ]).then(([c, s]) => {
      setCounts(c.data || []);
      setSkus(s.data || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const loadLines = async (countId) => {
    setLoadingLines(true);
    try {
      const r = await get('/inventory-control/stock-counts/'+countId+'/lines');
      setSelectedLines(r.data || []);
    } catch(e) { setSelectedLines([]); }
    finally { setLoadingLines(false); }
  };

  const selectCount = async (count) => {
    setSelected(count);
    await loadLines(count.id);
  };

  const openCreate = () => {
    setError('');
    setModal({ count_type:'cycle', notes:'', sku_ids:[] });
  };

  const createCount = async () => {
    setError('');
    if (!modal.count_type) { setError('Count type is required'); return; }
    setSaving(true);
    try {
      const r = await post('/inventory-control/stock-counts', modal);
      setModal(null);
      load();
      // Auto-open the new count
      selectCount(r.data);
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const updateLine = async (countId, lineId, physical_quantity, notes) => {
    try {
      await put(`/inventory-control/stock-counts/${countId}/lines/${lineId}`, {
        physical_quantity, notes
      });
      // Refresh lines
      await loadLines(countId);
    } catch(e) { alert(e.message); }
  };

  const filtered = counts.filter(c => !statusFilter || c.status === statusFilter);

  const variance_summary = selectedLines.reduce((acc, l) => {
    if (l.physical_quantity != null) {
      acc.counted++;
      if (parseFloat(l.variance||0) !== 0) acc.with_variance++;
    }
    return acc;
  }, { counted: 0, with_variance: 0 });

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Stock Counts</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Cycle, weekly and monthly physical inventory counts
          </p>
        </div>
        {canWrite && (
          <button className="btn btn-primary" onClick={openCreate}>+ New Count</button>
        )}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'300px 1fr',gap:20,alignItems:'start'}}>
        {/* Count list */}
        <div>
          {/* Status filter */}
          <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:12}}>
            <button className={'btn btn-sm '+(statusFilter===''?'btn-primary':'btn-outline')}
              onClick={()=>setStatusFilter('')}>All</button>
            {Object.entries(STATUS_COLORS).map(([k,v])=>(
              <button key={k}
                className={'btn btn-sm '+(statusFilter===k?'btn-primary':'btn-outline')}
                onClick={()=>setStatusFilter(statusFilter===k?'':k)}
                style={statusFilter===k?{}:{borderColor:v.color,color:v.color,fontSize:10}}>
                {v.label}
              </button>
            ))}
          </div>

          {loading ? <div className="spinner"/> : (
            <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
              {filtered.length === 0 ? (
                <div style={{padding:24,textAlign:'center',color:'#8A8A8A',fontSize:13}}>
                  No stock counts yet
                </div>
              ) : filtered.map(c => {
                const sc = STATUS_COLORS[c.status] || STATUS_COLORS.draft;
                const pct = c.line_count > 0 ? Math.round(c.counted_lines / c.line_count * 100) : 0;
                return (
                  <div key={c.id} onClick={()=>selectCount(c)}
                    style={{padding:'12px 16px',borderBottom:'1px solid #F2EFE7',cursor:'pointer',
                      background:selected?.id===c.id?'#F2EFE7':'#fff'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                      <div style={{fontWeight:700,fontSize:12,fontFamily:'monospace',color:'#3B2A21'}}>
                        {c.count_number}
                      </div>
                      <span style={{...sc,fontSize:9,fontWeight:700,padding:'2px 6px',
                        borderRadius:20,textTransform:'uppercase'}}>
                        {sc.label}
                      </span>
                    </div>
                    <div style={{fontSize:11,color:'#8A8A8A',marginBottom:4}}>
                      {COUNT_TYPES.find(t=>t.value===c.count_type)?.label} · {new Date(c.count_date).toLocaleDateString('en-NG')}
                    </div>
                    <div style={{background:'#F2EFE7',borderRadius:4,overflow:'hidden',height:4,marginBottom:4}}>
                      <div style={{height:4,background:'#556B2F',width:pct+'%'}}/>
                    </div>
                    <div style={{fontSize:10,color:'#8A8A8A'}}>
                      {c.counted_lines}/{c.line_count} SKUs counted ({pct}%)
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Count detail / lines */}
        <div>
          {!selected ? (
            <div className="empty-state">
              <h3>Select a count</h3>
              <p>Select a stock count from the list to view and enter physical counts</p>
            </div>
          ) : (
            <div>
              <div style={{background:'#fff',border:'1px solid var(--border-light)',
                borderRadius:10,padding:16,marginBottom:16}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:16,color:'#3B2A21'}}>{selected.count_number}</div>
                    <div style={{fontSize:12,color:'#8A8A8A',marginTop:2}}>
                      {COUNT_TYPES.find(t=>t.value===selected.count_type)?.label} ·
                      {new Date(selected.count_date).toLocaleDateString('en-NG')} ·
                      Initiated by {selected.initiated_by_name}
                    </div>
                  </div>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    {canApprove && selected.status === 'pending_approval' && (
                      <>
                        <button className="btn btn-primary btn-sm"
                          style={{background:'#2E6B3A',border:'none'}}
                          onClick={async()=>{
                            if (!window.confirm('Approve this stock count?')) return;
                            try {
                              await put('/inventory-control/stock-counts/'+selected.id+'/approve', {});
                              load();
                              setSelected({...selected, status:'approved'});
                            } catch(e) { alert(e.message); }
                          }}>Approve</button>
                        <button className="btn btn-outline btn-sm" style={{color:'#8F2E24',borderColor:'#8F2E24'}}
                          onClick={async()=>{
                            const reason = window.prompt('Rejection reason:');
                            if (!reason) return;
                            try {
                              await put('/inventory-control/stock-counts/'+selected.id+'/reject', {reason});
                              load();
                              setSelected({...selected, status:'rejected'});
                            } catch(e) { alert(e.message); }
                          }}>Reject</button>
                      </>
                    )}
                    {canWrite && selected.status === 'in_progress' && (
                      <button className="btn btn-primary btn-sm"
                        onClick={async()=>{
                          if (variance_summary.with_variance > 0 &&
                            !window.confirm(`${variance_summary.with_variance} SKUs have variances. Submit anyway?`)) return;
                          try {
                            await put('/inventory-control/stock-counts/'+selected.id+'/submit', {});
                            load();
                            setSelected({...selected, status:'pending_approval'});
                          } catch(e) { alert(e.message); }
                        }}>Submit for Approval</button>
                    )}
                  </div>
                </div>

                {/* Summary */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginTop:14}}>
                  {[
                    ['Total SKUs', selectedLines.length, '#556B2F'],
                    ['Counted', variance_summary.counted, '#2E6B3A'],
                    ['Pending', selectedLines.length - variance_summary.counted, '#8B5E0A'],
                    ['With Variance', variance_summary.with_variance, variance_summary.with_variance>0?'#8F2E24':'#2E6B3A'],
                  ].map(([label, value, color]) => (
                    <div key={label} style={{background:'#F9F8F5',borderRadius:8,padding:'10px 12px'}}>
                      <div style={{fontSize:9,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:2}}>{label}</div>
                      <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.4rem',fontWeight:700,color}}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Count lines */}
              {loadingLines ? <div className="spinner"/> : (
                <div className="tbl-wrap">
                  <table>
                    <thead><tr style={{background:'#556B2F'}}>
                      <th style={{color:'#F2EFE7'}}>SKU</th>
                      <th style={{color:'#F2EFE7'}}>Description</th>
                      <th style={{color:'#F2EFE7'}}>Type</th>
                      <th style={{textAlign:'right',color:'#F2EFE7'}}>System Qty</th>
                      <th style={{textAlign:'right',color:'#F2EFE7'}}>Physical Count</th>
                      <th style={{textAlign:'right',color:'#F2EFE7'}}>Variance</th>
                      <th style={{color:'#F2EFE7'}}>Notes</th>
                    </tr></thead>
                    <tbody>
                      {selectedLines.map(line => {
                        const variance = line.physical_quantity != null
                          ? parseFloat(line.physical_quantity) - parseFloat(line.system_quantity||0)
                          : null;
                        const canEdit = canWrite && ['in_progress','draft'].includes(selected.status);
                        return (
                          <tr key={line.id} style={{
                            background: line.physical_quantity == null ? '#fff' :
                              variance === 0 ? '#F6FFF6' : '#FFFBF0'
                          }}>
                            <td style={{fontSize:11,fontFamily:'monospace',fontWeight:700,color:'#3B2A21'}}>
                              {line.sku}
                            </td>
                            <td style={{fontSize:12}}>{line.description}</td>
                            <td>
                              <span style={{fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:20,
                                textTransform:'uppercase',
                                background:line.inventory_type==='raw'?'#EDF4FF':line.inventory_type==='prepared'?'#E8F5EB':'#FFF3DC',
                                color:line.inventory_type==='raw'?'#1E4FAD':line.inventory_type==='prepared'?'#2E6B3A':'#8B5E0A'}}>
                                {line.inventory_type}
                              </span>
                            </td>
                            <td className="amt">{Number(line.system_quantity||0).toLocaleString()}</td>
                            <td>
                              {canEdit ? (
                                <input type="number"
                                  defaultValue={line.physical_quantity ?? ''}
                                  onBlur={e => {
                                    if (e.target.value !== '') {
                                      updateLine(selected.id, line.id, e.target.value, line.notes);
                                    }
                                  }}
                                  style={{width:80,padding:'4px 8px',border:'1px solid #E8E0D4',
                                    borderRadius:4,fontSize:12,textAlign:'right'}}
                                  min="0" step="0.01" />
                              ) : (
                                <div style={{textAlign:'right',fontSize:12,fontWeight:600}}>
                                  {line.physical_quantity != null ? Number(line.physical_quantity).toLocaleString() : '—'}
                                </div>
                              )}
                            </td>
                            <td style={{textAlign:'right',fontWeight:700,fontSize:12,
                              color: variance == null ? '#8A8A8A' : variance < 0 ? '#8F2E24' : variance > 0 ? '#8B5E0A' : '#2E6B3A'}}>
                              {variance == null ? '—' : (variance > 0 ? '+' : '') + Number(variance).toLocaleString()}
                            </td>
                            <td>
                              {canEdit ? (
                                <input
                                  defaultValue={line.notes||''}
                                  onBlur={e => {
                                    if (line.physical_quantity != null) {
                                      updateLine(selected.id, line.id, line.physical_quantity, e.target.value);
                                    }
                                  }}
                                  style={{width:'100%',padding:'4px 8px',border:'1px solid #E8E0D4',
                                    borderRadius:4,fontSize:11}}
                                  placeholder="Notes..." />
                              ) : (
                                <span style={{fontSize:11,color:'#8A8A8A'}}>{line.notes||'—'}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-hdr">
              <span className="modal-title">New Stock Count</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}

              <div className="form-group">
                <label className="form-label">Count Type *</label>
                <select className="form-control" value={modal.count_type}
                  onChange={e=>setModal({...modal,count_type:e.target.value})}>
                  {COUNT_TYPES.map(t=>(
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Scope</label>
                <select className="form-control" value={modal.scope||'all'}
                  onChange={e=>setModal({...modal,scope:e.target.value,sku_ids:[]})}>
                  <option value="all">All Active SKUs</option>
                  <option value="raw">Raw Materials Only</option>
                  <option value="prepared">Prepared Materials Only</option>
                  <option value="select">Select Specific SKUs</option>
                </select>
              </div>

              {modal.scope === 'select' && (
                <div className="form-group">
                  <label className="form-label">Select SKUs</label>
                  <div style={{maxHeight:200,overflowY:'auto',border:'1px solid #E8E0D4',
                    borderRadius:8,padding:8}}>
                    {skus.map(s=>(
                      <label key={s.id} style={{display:'flex',alignItems:'center',gap:8,
                        padding:'4px 0',cursor:'pointer',fontSize:12}}>
                        <input type="checkbox"
                          checked={modal.sku_ids?.includes(s.id)}
                          onChange={e=>{
                            const ids = modal.sku_ids || [];
                            setModal({...modal,
                              sku_ids: e.target.checked
                                ? [...ids, s.id]
                                : ids.filter(id=>id!==s.id)
                            });
                          }}
                          style={{accentColor:'#556B2F'}} />
                        <span style={{fontFamily:'monospace',color:'#1E4FAD'}}>{s.sku}</span>
                        <span style={{color:'#3B2A21'}}>{s.description}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={modal.notes||''}
                  onChange={e=>setModal({...modal,notes:e.target.value})}
                  placeholder="Any notes about this count..." />
              </div>

              <div style={{background:'#F9F8F5',borderRadius:8,padding:12,fontSize:12,color:'#555'}}>
                💡 System quantities will be recorded at the time of count creation.
                Enter physical counts against each SKU after creating the count.
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={createCount} disabled={saving}>
                {saving?'Creating...':'Create Count'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
